// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./JobTitlesConsole.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import NewJobTitlesDialog from "./NewJobTitlesDialog/NewJobTitlesDialog";
import EditJobTitlesDialog from "./EditJobTitlesDialog/EditJobTitlesDialog";
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import {
	checkboxSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

const JobTitlesConsole = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	// Data
	const [jobTitles, setJobTitles] = useState([]);
	const [departments, setDepartments] = useState([]);

	// Table
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);

	// UI State
	const [loading, setLoading] = useState(false);
	const [newOpen, setNewOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [recordToEdit, setRecordToEdit] = useState(null);

	// Access log
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Job Titles Console",
			employeeID: employeeID,
			notes: "Accessed the Job Titles Console page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Fetch on mount
	useEffect(() => {
		fetchJobTitles();
		fetchDepartments();
	}, []);

	// Rebuild table when data changes
	useEffect(() => {
		if (jobTitles.length > 0 && departments.length > 0) {
			buildColumns();
			buildRows();
		}
	}, [jobTitles, departments]);

	// API calls
	const fetchJobTitles = async () => {
		setLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobTitles/all"
			);
			setJobTitles(res.data);
		} catch (err) {
			console.error("Error fetching job titles:", err);
		}
		setLoading(false);
	};

	const fetchDepartments = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/departments/all"
			);
			setDepartments(res.data);
		} catch (err) {
			console.error("Error fetching departments:", err);
		}
	};

	// Dialog handlers
	const openNew = () => setNewOpen(true);
	const closeNew = () => setNewOpen(false);

	const openEdit = (rec) => {
		setRecordToEdit(rec);
		setEditOpen(true);
	};
	const closeEdit = () => setEditOpen(false);

	// Build table columns
	const buildColumns = () => {
		setColumns([
			{
				accessorKey: "name",
				header: () => <span>Name</span>,
				label: "Name",
				cell: (props) => (
					<span>{props.getValue() ?? notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "department_name",
				header: () => <span>Department</span>,
				label: "Department",
				cell: (props) => (
					<span>{props.getValue() ?? notApplicable}</span>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: departments.map((dept) => ({
					label: dept.name,
					value: dept.name,
				})),
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: ({ row }) => (
					<IconButton
						onClick={() => openEdit(row.original.record)}
						size="small"
						disableRipple
						sx={{ justifyContent: "flex-end" }}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	// Build table rows
	const buildRows = () => {
		setRows(
			jobTitles.map((jt) => ({
				name: jt.name,
				department_name: jt.department_name,
				edit: "",
				record: jt,
			}))
		);
	};

	return (
		<div className="JobTitlesConsole">
			<div className="JobTitlesConsoleHeader">
				<div className="JobTitlesConsoleTitle">
					Job Titles Console
					<Button
						variant="contained"
						color="primary"
						onClick={openNew}
						disabled={loading}
						sx={{ ml: 2 }}
					>
						New Record
					</Button>
				</div>
			</div>

			{loading ? (
				<div className="loadingDiv">
					<CircularProgress />
				</div>
			) : jobTitles.length === 0 ? (
				<J2NoResultsFound />
			) : (
				<div className="tableContainerDiv">
					<TanStackTable columnData={columns} rowData={rows} />
				</div>
			)}

			<NewJobTitlesDialog
				open={newOpen}
				departments={departments}
				newJobTitlesDialogClosed={closeNew}
				fetchJobTitles={fetchJobTitles}
			/>

			<EditJobTitlesDialog
				open={editOpen}
				departments={departments}
				fetchJobTitles={fetchJobTitles}
				editRecordClosed={closeEdit}
				recordToEdit={recordToEdit}
			/>
		</div>
	);
};

export default JobTitlesConsole;
